import * as IoC from 'typescript-ioc';
import React, { useEffect, useState } from 'react';
import * as mobxReact from 'mobx-react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { DefaultCloseSubWindowButton } from '../../../../global/sub-window/common/common-close-sub-window-buttons';
import OptionsButton from '../../../../global/options-button/options-button';
import { CommonPopupBodyFooterLayout } from '../../../../global/common-popup/common-popup-body-footer-layout';
import './attendee-form.scss';
import { Permission } from '../../../../../api/roles.dao';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import { AddNewEntityButton } from '../../../../global/add-new-entity/add-new-entity-button';
import { EventExecutionRuntime } from '../../../../../domain/jobs/implementations/event-execution-runtime';
import { AttendeeFormBodyPresentation } from './attendee-form-body-presentation';
import { AttendeeFormViewModel } from './attendee-form.viewmodel';
import { MORE_BUTTON_TEXT, cancelText, submitText } from '../../../../../constants/common-labels.constants';

const AttendeeFormPresentation = injectIntl(mobxReact.observer((props: { vm: AttendeeFormViewModel; intl: IntlShape }) => {
    const [organizatinsList, setOrganizationsList] = useState([]);
    const [isOrgLoaded, setIsOrgLoaded] = useState(false);

    const formVM = props.vm;
    const intl = props.intl;
    const formData = formVM.getAttendeeFormDataVM();

    useEffect(() => {
        if (formData && !isOrgLoaded) {
            setOrganizationsList(() => formData.getOrganizations());
            setIsOrgLoaded(() => true);
        }

        if (isOrgLoaded && formData && organizatinsList.length !== formData?.getOrganizations().length) {
            const newOrganizationsList = formData.getOrganizations();
            setOrganizationsList(prevOrganizationsList => {
                const addedOrg = newOrganizationsList.filter(item => !prevOrganizationsList.some(elem => elem.organizationId === item.organizationId));
                formData.setOrganizationId(addedOrg[0].organizationId);
                formData.setPriorityInOrganization(null);
                return newOrganizationsList;
            });
        }
    }, [formData?.getOrganizations().length, organizatinsList, isOrgLoaded]);

    if (!formData) {
        return <CommonPopupBodyFooterLayout
            body={
                <div className='oe-form meeting-form'>
                    <FormattedMessage
                        description='Label text'
                        defaultMessage='Loading attendee data{suffix}'
                        id='oe.attendee.loadingData'
                        values={{
                            suffix: '...'
                        }}
                    />
                </div>
            }
        />;
    }

    const attendeeOptions = [
        hasPermission(Permission.DELETE_ATTENDEE) && {
            label: intl.formatMessage({
                id: 'oe.attendee.delete',
                defaultMessage: 'Delete Attendee',
                description: 'Button text'
            }),
            disabled: !IoC.Container.get(EventExecutionRuntime).isReady(),
            action: formVM.deleteAttendee,
            danger: true
        }
    ].filter(Boolean);

    return (
        <CommonPopupBodyFooterLayout
            body={<AttendeeFormBodyPresentation
                formData={formData}
                formVM={formVM}
                AddNewOrganizationButton={() => <AddNewEntityButton
                    onClick={() => formVM.createOrganizationFromForm(formVM.fetchOrganizations.bind(formVM))}
                    permission={Permission.CREATE_ORGANIZATION}
                    label={<FormattedMessage
                        description='Label text'
                        defaultMessage='{prefix}New Organization'
                        id='oe.organization.new'
                        values={{
                            prefix: '+ '
                        }}
                    />}
                />}
            />}
            footer={
                <>
                    {formVM.isSaving() && <span className='oe-popup-layout__footer-status-area'>
                        <FormattedMessage
                            id='oe.attendee.attendeeDataIsLoading'
                            description='Preloader message text'
                            defaultMessage='Attendee data is loading, please wait{suffix}'
                            values={{
                                suffix: '...'
                            }}
                        />
                    </span>}
                    <div className='buttons-block buttons-block--from-right'>
                        <DefaultCloseSubWindowButton
                            text={submitText(!!formVM.getAttendeeId())}
                            modifier={['fat', 'confirm-safe']}
                            onClick={formVM.formSubmitAction}
                            disabled={formVM.isSaving() || formVM.getAttendeeFormDataVM().isImageUploading()}
                        />
                        <DefaultCloseSubWindowButton text={cancelText()} />
                        {formVM.getAttendeeId() && attendeeOptions.length > 0 &&
                            <OptionsButton
                                className='options-button--right-margin-auto'
                                modifier={['fat', 'options']}
                                label={<FormattedMessage {...MORE_BUTTON_TEXT} />}
                                options={attendeeOptions}
                                position='top'
                                align='start'
                            />
                        }
                    </div>
                </>
            }
        />
    );
}));

export { AttendeeFormPresentation };
